import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import { EditHrEmployeeSelectField } from '../../../EditHrEmployeeSelectField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const OWNERSHIP_FIELDS: FieldMapping[] = [
  { apiField: 'ownerId', formField: 'ownerId' },
  { apiField: 'ownership', formField: 'ownership' },
  { apiField: 'owningUser', formField: 'owningUserId' },
  { apiField: 'placementAgent', formField: 'placementAgent' },
  {
    apiField: 'placementAgentDateAssigned',
    formField: 'placementAgentDateAssigned',
    isDate: true
  },
  { apiField: 'placementAgentGlobal', formField: 'placementAgentGlobal' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      changes[apiField] = currentVal === '' ? null : (currentVal as string);
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        ownership
        ownerId {
          id
          name
        }
        owningUser {
          id
          name
        }
        placementAgent
        placementAgentDateAssigned
        placementAgentGlobal
      }
    }
  }
`;

export interface EditOwnershipDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditOwnershipDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditOwnershipDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(OWNERSHIP_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save ownership & placement info');
      } else {
        toast.success('Ownership & placement info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving ownership & placement info:', error);
      toast.error('Failed to save ownership & placement info');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Ownership & Placement"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditHrEmployeeSelectField
          label="Owner"
          onChange={(id, name) => {
            onChange({
              ...value,
              owner: name,
              ownerId: id
            });
          }}
          value={
            value.ownerId
              ? {
                  id: value.ownerId,
                  name: value.owner
                }
              : null
          }
        />
        <EditDynamicSelectField
          fieldName="ownership"
          label="Ownership"
          model="opstream.account"
          onChange={v => {
            updateField('ownership', v);
          }}
          value={value.ownership}
        />
        <EditHrEmployeeSelectField
          label="Owning User"
          onChange={(id, name) => {
            onChange({
              ...value,
              owningUser: name,
              owningUserId: id
            });
          }}
          value={
            value.owningUserId
              ? {
                  id: value.owningUserId,
                  name: value.owningUser
                }
              : null
          }
        />
        <EditDynamicSelectField
          fieldName="placement_agent"
          label="Placement Agent"
          model="opstream.account"
          onChange={v => {
            const updates: Partial<AccountOthersData> = {
              placementAgent: v
            };
            // Only set date if Placement Agent was null/empty and now being set
            if (v && !initialValue.placementAgent) {
              updates.placementAgentDateAssigned = new Date()
                .toISOString()
                .split('T')[0];
            }
            onChange({ ...value, ...updates });
          }}
          value={value.placementAgent}
        />
        <EditFormField
          label="Placement Agent Date Assigned"
          onChange={v => {
            updateField('placementAgentDateAssigned', v);
          }}
          type="date"
          value={toDateInputValue(value.placementAgentDateAssigned)}
        />
        <EditDynamicSelectField
          fieldName="placement_agent_global"
          label="Placement Agent Global"
          model="opstream.account"
          onChange={v => {
            updateField('placementAgentGlobal', v);
          }}
          value={value.placementAgentGlobal}
        />
      </div>
    </EditDialog>
  );
}
